import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../utils/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ILMSButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-brand-green-700 text-white hover:bg-brand-green-800 active:bg-brand-green-900 disabled:bg-brand-green-300',
  secondary: 'bg-brand-gold-500 text-white hover:bg-brand-gold-600 active:bg-brand-gold-700 disabled:bg-brand-gold-200',
  outline: 'bg-white text-brand-green-700 border border-ink-300/50 hover:bg-brand-green-50 disabled:text-ink-300',
  ghost: 'bg-transparent text-ink-700 hover:bg-surface-muted disabled:text-ink-300',
  danger: 'bg-critical-600 text-white hover:bg-critical-700 active:bg-critical-700 disabled:bg-critical-100',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-4 text-sm gap-2',
  lg: 'h-12 px-5 text-base gap-2',
};

export const ILMSButton = React.forwardRef<HTMLButtonElement, ILMSButtonProps>(function ILMSButton(
  { variant = 'primary', size = 'md', loading, icon, iconPosition = 'left', fullWidth, className, children, disabled, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-semibold transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-green-500',
        'disabled:cursor-not-allowed select-none min-h-touch',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        fullWidth && 'w-full',
        className
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        icon && iconPosition === 'left' && <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
      )}
      {children && <span className="truncate">{children}</span>}
      {!loading && icon && iconPosition === 'right' && <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">{icon}</span>}
    </button>
  );
});
